import React from 'react';
import PropTypes from 'prop-types';

import AvailableLanguageLink from '../AvailableLanguageLink';
import { Link } from 'src/components/Inputs';

/*
 * When making changes to this file be sure that any changes
 * also are made to the corresponding iOS specific counterpart
 * in src/pages/<lang>/legal/ios.jsx.
 */

const propTypes = {
  id: PropTypes.string,
  availableLanguages: PropTypes.arrayOf(
    PropTypes.shape({
      href: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
    }),
  ),
};

const DeVersion = ({ id, availableLanguages }) => {
  return (
    <>
      <h4
        id={id || 'cookie-richtlinie'}
        data-magellan-target={id || 'cookie-richtlinie'}
      >
        Cookie-Richtlinie
      </h4>

      <p>Verfügbare Sprachen:</p>
      <ul>
        {availableLanguages.map((language, i) => {
          return (
            <AvailableLanguageLink
              key={`language-${i}`}
              href={language.href}
              label={language.label}
            />
          );
        })}
      </ul>
      <br />
      <br />

      <p>Datum des Inkrafttretens: 22. August 2023</p>
      <p>
        In dieser Cookie-Richtlinie (die „Richtlinie“) wird erläutert, wie Plaid
        Inc. und seine Tochtergesellschaften, einschließlich Plaid Financial
        Ltd., Plaid Consumer Reporting Agency, Inc., und Plaid, B.V. (zusammen
        „wir“, „unser“ oder „uns“), und zuverlässige Dritte Cookies auf unseren
        Websites und anderen Online-Diensten (zusammen die „Dienste“) einsetzen,
        und welche Möglichkeiten Sie in Bezug auf diese Cookies haben. Lesen Sie
        bitte diese Richtlinie, um mehr über die Arten von Cookies zu erfahren,
        die wir einsetzen, warum wir sie einsetzen und wie Sie die Verwendung
        von Cookies kontrollieren oder einschränken können.
      </p>
      <p>
        Weitere Informationen über unsere Datenschutzpraktiken finden Sie in
        unserer{' '}
        <Link href='#datenschutzrichtlinie-für-endnutzer'>
          <a>Datenschutzrichtlinie für Endnutzer</a>
        </Link>{' '}
        und in unserer{' '}
        <Link href='#datenschutzerklarung'>
          <a>Datenschutzerklärung</a>
        </Link>
        .
      </p>

      <p>
        <em>Zum Abschnitt springen:</em>
      </p>
      <p>
        <Link href='#was-sind-cookies-cookie-richtlinie'>
          <a data-smooth-scroll data-offset='80'>
            Was sind Cookies?
          </a>
        </Link>
      </p>

      <p>
        <Link href='#wie-wir-cookies-verwenden-cookie-richtlinie'>
          <a data-smooth-scroll data-offset='80'>
            Wie wir Cookies verwenden
          </a>
        </Link>
      </p>

      <p>
        <Link href='#ihre-wahlmoglichkeiten-cookie-richtlinie'>
          <a data-smooth-scroll data-offset='80'>
            Ihre Wahlmöglichkeiten
          </a>
        </Link>
      </p>

      <p>
        <Link href='#anderungen-an-dieser-richtlinie-cookie-richtlinie'>
          <a data-smooth-scroll data-offset='80'>
            Änderungen an dieser Richtlinie
          </a>
        </Link>
      </p>

      <p>
        <Link href='#kontaktieren-sie-uns-cookie-richtlinie'>
          <a data-smooth-scroll data-offset='80'>
            Kontaktieren Sie uns
          </a>
        </Link>
      </p>

      <h5 id='was-sind-cookies-cookie-richtlinie'>Was sind Cookies?</h5>
      <p>
        Cookies sind kleine Datendateien, die in Ihrem Browser oder auf Ihrem
        Gerät gespeichert werden. Sie können in Form von Session-Cookies (die
        ablaufen, sobald Sie Ihren Webbrowser schließen) oder als permanente
        Cookies (die für einen bestimmten Zeitraum oder bis zu ihrer Löschung in
        Ihrem Browser gespeichert bleiben) vorliegen. Sie enthalten in der Regel
        Informationen wie Website-Präferenzen, Benutzereinstellungen und den
        Browserverlauf. Anhand dieser Informationen können wir Sie erkennen,
        wenn Sie mit unseren Diensten in Kontakt treten oder diese erneut
        besuchen.
      </p>
      <p>
        Cookies können entweder von dem Unternehmen, das die von Ihnen besuchte
        Website betreibt („Erstanbieter-Cookies“) oder von anderen Unternehmen
        („Drittanbieter-Cookies“) gesetzt werden. Wir nutzen beispielsweise
        Erstanbieter-Cookies, um Ihre Einstellungen und Präferenzen, wie Sprache
        und Standort, zu speichern. Wir binden auch Analyse-Cookies von
        Drittanbietern, wie z. B. Google Analytics, in unsere Websites ein, um
        zu erfahren, wie Sie unsere Websites nutzen, damit wir sie verbessern
        können.
      </p>
      <p>
        Außerdem verwenden wir andere Technologien, die ähnlich wie Cookies
        funktionieren. So setzen wir beispielsweise Web-Beacons (auch Pixel
        genannt) ein, d. h. kleine Bilder auf einer Webseite oder in einer
        E-Mail. Web Beacons erfassen Informationen über Ihren Browser oder Ihr
        Gerät und können Cookies setzen. Darüber hinaus benutzen wir die lokale
        Speicherung, mit der Daten lokal auf Ihrem Browser oder Gerät
        gespeichert werden können.
      </p>

      <h5 id='wie-wir-cookies-verwenden-cookie-richtlinie'>
        Wie wir Cookies verwenden
      </h5>
      <p>
        Die Verwendung von Cookies erfolgt aus verschiedenen Gründen, z. B. um
        die Beliebtheit bestimmter Inhalte zu ermitteln, um unsere Dienste und
        Ihr Erlebnis zu verbessern und um Ihre Online-Aktivitäten besser zu
        verstehen. Die von uns verwendeten Cookies fallen im Allgemeinen in eine
        oder mehrere der folgenden Kategorien:
      </p>
      <div className='eupp-container'>
        <table>
          <thead>
            <tr>
              <th>Cookie-Kategorien</th>
              <th>Wie wir diese Cookies verwenden</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Unbedingt erforderlich</td>
              <td>
                Diese Cookies sind für das ordnungsgemäße Funktionieren unserer
                Dienste unbedingt erforderlich. Ein Teil unserer Dienste hängt
                von diesen Cookies ab und funktioniert möglicherweise nicht
                richtig oder gar nicht, wenn die Cookies deaktiviert sind.
              </td>
            </tr>
            <tr>
              <td>Funktional</td>
              <td>
                Diese Cookies ermöglichen es der Website, erweiterte
                Funktionalität und Personalisierung bereitzustellen. Sie können
                von uns oder von Drittanbietern festgelegt werden, deren Dienste
                wir unseren Seiten hinzugefügt haben. Wenn Sie diese Cookies
                nicht zulassen, funktionieren einige oder alle dieser Dienste
                möglicherweise nicht ordnungsgemäß.
              </td>
            </tr>
            <tr>
              <td>Leistung</td>
              <td>
                Wir verwenden diese Cookies, um ein besseres Verständnis dafür
                zu bekommen, wie Sie mit unseren Diensten interagieren, damit
                wir sie verbessern können.
                <br />
                <br />
                Wir können diese Cookies zum Beispiel einsetzen, um
                festzustellen, ob Sie mit bestimmten Inhalten oder Funktionen in
                Kontakt getreten sind. Wir können mithilfe dieser Cookies auch
                mehr darüber erfahren, welche Funktionen bei unseren Nutzern am
                beliebtesten sind und wo wir eventuell Verbesserungen vornehmen
                müssen.
              </td>
            </tr>
            <tr>
              <td>Targeting/Werbung</td>
              <td>
                Wir und unsere zuverlässigen Werbepartner verwenden diese
                Cookies, um Werbung anzuzeigen, um die Werbung für die Besucher
                unserer Websites relevanter zu gestalten und um die Effektivität
                von jeglichen Werbeaktionen sowohl auf unseren Diensten als auch
                auf anderen Websites zu verfolgen.
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <h5 id='ihre-wahlmoglichkeiten-cookie-richtlinie'>
        Ihre Wahlmöglichkeiten
      </h5>
      <p>
        Sie können selbst entscheiden, ob Sie Cookies akzeptieren möchten oder
        nicht. Da Cookies jedoch ein wichtiger Bestandteil der Funktionsweise
        unserer Dienste sein können, kann das Löschen oder Ablehnen von Cookies
        die Verfügbarkeit und Funktionalität unserer Dienste einschränken.
      </p>
      <p>
        Nachstehend finden Sie einige Optionen, mit denen Sie die Verwendung von
        Cookies in unseren Diensten kontrollieren oder einschränken können:
      </p>
      <ul>
        <li>
          <p>
            Normalerweise können Sie in den Konfigurationseinstellungen Ihres
            Browsers die Einstellungen zum Entfernen oder Ablehnen von
            Browser-Cookies manuell festlegen. Um diese Einstellungen zu ändern,
            müssen Sie die Anweisungen Ihres Browsers befolgen.
          </p>
        </li>
        <li>
          <p>
            Wenn Sie verhindern möchten, dass Ihre Daten von Google Analytics
            verwendet werden, können Sie das{' '}
            <Link href='https://tools.google.com/dlpage/gaoptout?hl=nl'>
              <a>Opt-Out-Browser-Add-on von Google</a>
            </Link>{' '}
            installieren.
          </p>
        </li>
        <li>
          <p>
            Hinweise dazu, wie unsere Werbepartner es Ihnen ermöglichen können,
            den Erhalt von Werbung auf der Grundlage Ihres Browserverlaufs zu
            unterbinden, finden Sie unter{' '}
            <Link href='http://optout.aboutads.info/'>
              <a>http://optout.aboutads.info/</a>
            </Link>
            . Darüber hinaus können europäische Nutzer die{' '}
            <Link href='http://www.youronlinechoices.eu/'>
              <a>European Interactive Digital Advertising Alliance</a>
            </Link>{' '}
            und kanadische Nutzer die{' '}
            <Link href='https://youradchoices.ca/'>
              <a>Digital Advertising Alliance of Canada</a>
            </Link>{' '}
            besuchen, um mehr darüber zu erfahren, wie man diese Art von Werbung
            ablehnen kann.
          </p>
        </li>
      </ul>

      <h5 id='anderungen-an-dieser-richtlinie-cookie-richtlinie'>
        Änderungen an dieser Richtlinie
      </h5>
      <p>
        Wir können die Cookies (oder ähnliche Technologien), die wir verwenden,
        von Zeit zu Zeit aktualisieren oder ändern. Die jeweils aktuellste
        Version dieser Richtlinie wird auf unserer Website unter{' '}
        <Link href='https://plaid.com/legal'>
          <a>https://plaid.com/legal</a>
        </Link>{' '}
        veröffentlicht. Sollten wir Änderungen vornehmen, werden wir das
        Gültigkeitsdatum oben in der Richtlinie aktualisieren. Wir empfehlen
        Ihnen, in regelmäßigen Abständen nachzusehen, ob es Änderungen gibt.
      </p>

      <h5 id='kontaktieren-sie-uns-cookie-richtlinie'>Kontaktieren Sie uns</h5>
      <p>
        Bei Fragen zu unserer Verwendung von Cookies oder dieser Richtlinie
        wenden Sie sich bitte unter{' '}
        <Link href='mailto:privacy@plaid.com'>
          <a>privacy@plaid.com</a>
        </Link>{' '}
        an uns.
      </p>

      <hr className='section-divider' />
    </>
  );
};

DeVersion.propTypes = propTypes;
export default DeVersion;
